import React, { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { NavigationContainer } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { isKioskMode } from './src/config/kioskConfig';
import { RootNavigator } from './src/navigation/RootNavigator';
import { KioskNavigator } from './src/navigation/KioskNavigator';
import { AuthProvider, useAuth } from './src/context/AuthContext';
import { MenuDrawerProvider } from './src/context/MenuDrawerContext';
import { NotificationCountProvider } from './src/context/NotificationCountContext';
import { PendingRequestCountProvider } from './src/context/PendingRequestCountContext';
import {
  PreferencesProvider,
  usePreferences,
} from './src/context/PreferencesContext';
import { NetworkConnectivityProvider } from './src/context/NetworkConnectivityContext';
import { ReportDownloadProvider } from './src/context/ReportDownloadContext';
import { MobileAttendanceFaceVerification } from './src/components/MobileAttendanceFaceVerification';
import { LocationTrackingBootstrap } from './src/components/LocationTrackingBootstrap';
import { AppLoader } from './src/components/AppLoader';
import { AppScreen } from './src/components/AppScreen';
import { MenuDrawer } from './src/components/MenuDrawer';
import { MandatoryUpdateGate } from './src/components/MandatoryUpdateGate';
import { OperationFeedbackHost } from './src/components/OperationFeedbackHost';
import { NoInternetConnectionGate } from './src/screens/system/NoInternetConnectionScreen';
import { SplashScreen } from './src/screens/system/SplashScreen';
import { spacing } from './src/constants/spacing';
import {
  createAppLinking,
  createKioskLinking,
  isNavigationBootstrapReady,
} from './src/navigation/linking';
import { navigationRef } from './src/navigation/navigationRef';

function AppNavigation() {
  const { navigationTheme } = usePreferences();
  const { status } = useAuth();
  const linking = useMemo(
    () =>
      createAppLinking(
        status === 'authenticated' ? 'authenticated' : 'unauthenticated',
      ),
    [status],
  );

  // Wait for session restore before mounting NavigationContainer so linking
  // reads the current URL against the real Auth/MainTabs tree (refresh-safe).
  if (!isNavigationBootstrapReady(status)) {
    return <SplashScreen />;
  }

  return (
    <MenuDrawerProvider>
      <NavigationContainer
        ref={navigationRef}
        theme={navigationTheme}
        linking={linking}
        documentTitle={{
          formatter: (options, route) =>
            `${options?.title ?? route?.name ?? 'ArkHRMS'} · ArkHRMS`,
        }}
      >
        <RootNavigator />
        <MenuDrawer />
      </NavigationContainer>
    </MenuDrawerProvider>
  );
}

/**
 * Kiosk build: activation-gated walk-up attendance; Login opens employee HRMS.
 * After employee logout, the activated kiosk session is restored from Keychain.
 */
function KioskAppNavigation() {
  const { navigationTheme } = usePreferences();
  const auth = useAuth();
  const appLinking = useMemo(() => createAppLinking('authenticated'), []);
  const kioskLinking = useMemo(() => createKioskLinking(), []);

  if (!isNavigationBootstrapReady(auth.status)) {
    return (
      <AppScreen contentStyle={styles.center}>
        <AppLoader message="Starting…" />
      </AppScreen>
    );
  }

  if (auth.status === 'authenticated') {
    return (
      <NotificationCountProvider>
        <PendingRequestCountProvider>
          <ReportDownloadProvider>
            <MenuDrawerProvider>
              <NavigationContainer
                ref={navigationRef}
                theme={navigationTheme}
                linking={appLinking}
                documentTitle={{
                  formatter: (options, route) =>
                    `${options?.title ?? route?.name ?? 'ArkHRMS'} · ArkHRMS`,
                }}
              >
                <RootNavigator />
                <MenuDrawer />
              </NavigationContainer>
            </MenuDrawerProvider>
          </ReportDownloadProvider>
        </PendingRequestCountProvider>
      </NotificationCountProvider>
    );
  }

  return (
    <NavigationContainer
      theme={navigationTheme}
      linking={kioskLinking}
      documentTitle={{
        formatter: (options, route) =>
          `${options?.title ?? route?.name ?? 'Kiosk'} · ArkHRMS`,
      }}
    >
      <KioskNavigator />
    </NavigationContainer>
  );
}

function App(): React.JSX.Element {
  const kiosk = isKioskMode();
  if (kiosk) {
    return (
      <GestureHandlerRootView style={styles.root}>
      <SafeAreaProvider>
        <PreferencesProvider>
          <MandatoryUpdateGate>
            <NetworkConnectivityProvider>
              <AuthProvider>
                <KioskAppNavigation />
                <MobileAttendanceFaceVerification />
              </AuthProvider>
              <NoInternetConnectionGate />
            </NetworkConnectivityProvider>
          </MandatoryUpdateGate>
          <OperationFeedbackHost />
        </PreferencesProvider>
      </SafeAreaProvider>
      </GestureHandlerRootView>
    );
  }

  return (
    <GestureHandlerRootView style={styles.root}>
    <SafeAreaProvider>
      <PreferencesProvider>
        <MandatoryUpdateGate>
          <NetworkConnectivityProvider>
            <AuthProvider>
              <NotificationCountProvider>
                <PendingRequestCountProvider>
                  <ReportDownloadProvider>
                    <AppNavigation />
                    <MobileAttendanceFaceVerification />
                    <LocationTrackingBootstrap />
                  </ReportDownloadProvider>
                </PendingRequestCountProvider>
              </NotificationCountProvider>
            </AuthProvider>
            <NoInternetConnectionGate />
          </NetworkConnectivityProvider>
        </MandatoryUpdateGate>
        <OperationFeedbackHost />
      </PreferencesProvider>
    </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  root: Platform.select({
    web: { flex: 1, height: '100%', minHeight: 0, overflow: 'hidden' },
    default: { flex: 1 },
  }),
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
});

export default App;
